import React, { useState, useEffect } from "react";
import styled, { keyframes, css } from "styled-components";
import { motion, useAnimation } from "framer-motion";
import {
  FiDollarSign,
  FiTrendingUp,
  FiTrendingDown,
  FiPieChart,
  FiCalendar,
  FiFilter,
  FiBarChart2,
  FiSettings,
  FiBell,
  FiDownload,
  FiChevronRight,
  FiCreditCard,
  FiShoppingCart,
  FiHome,
  FiCoffee,
  FiActivity,
  FiCheckCircle,
  FiTarget,
} from "react-icons/fi";

const FinancesShowcase = () => {
  const [activeView, setActiveView] = useState("overview");
  const [selectedPeriod, setSelectedPeriod] = useState("month");
  const [budgetExceeded, setBudgetExceeded] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const chartControls = useAnimation();
  const spendingControls = useAnimation();
  const incomeControls = useAnimation();

  useEffect(() => {
    const checkScreenSize = () => {
      setIsSmallScreen(window.innerWidth <= 430);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);

    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  const spendingCategories = [
    {
      id: 1,
      name: "Food & Dining",
      shortName: "Food",
      amount: 420,
      color: "#EF4444",
      icon: <FiCoffee />,
      percentage: 28,
    },
    {
      id: 2,
      name: "Shopping",
      shortName: "Shopping",
      amount: 380,
      color: "#8B5CF6",
      icon: <FiShoppingCart />,
      percentage: 25,
    },
    {
      id: 3,
      name: "Housing",
      shortName: "Housing",
      amount: 1200,
      color: "#3B82F6",
      icon: <FiHome />,
      percentage: 20,
    },
    {
      id: 4,
      name: "Transportation",
      shortName: "Transport",
      amount: 180,
      color: "#10B981",
      icon: <FiCreditCard />,
      percentage: 12,
    },
    {
      id: 5,
      name: "Entertainment",
      shortName: "Entertainment",
      amount: 120,
      color: "#F59E0B",
      icon: <FiActivity />,
      percentage: 8,
    },
    {
      id: 6,
      name: "Other",
      shortName: "Other",
      amount: 75,
      color: "#6B7280",
      icon: <FiDollarSign />,
      percentage: 7,
    },
  ];

  const recentTransactions = [
    {
      id: 1,
      name: "Grocery Store",
      shortName: "Grocery",
      amount: -85.5,
      category: "Food & Dining",
      shortCategory: "Food",
      date: "Today",
      type: "expense",
    },
    {
      id: 2,
      name: "Salary Deposit",
      shortName: "Salary",
      amount: 3500,
      category: "Income",
      shortCategory: "Income",
      date: "Today",
      type: "income",
    },
    {
      id: 3,
      name: "Amazon Purchase",
      shortName: "Amazon",
      amount: -129.99,
      category: "Shopping",
      shortCategory: "Shop",
      date: "Yesterday",
      type: "expense",
    },
    {
      id: 4,
      name: "Coffee Shop",
      shortName: "Coffee",
      amount: -6.75,
      category: "Food & Dining",
      shortCategory: "Food",
      date: "2 days ago",
      type: "expense",
    },
    {
      id: 5,
      name: "Freelance Work",
      shortName: "Freelance",
      amount: 850,
      category: "Income",
      shortCategory: "Income",
      date: "3 days ago",
      type: "income",
    },
  ];

  const features = [
    {
      id: 1,
      icon: <FiBarChart2 />,
      title: "Smart Expense Tracking",
      shortTitle: "Expense Tracking",
      description:
        "Register every income and expense with custom categories and notes. Get instant insights into your spending patterns.",
      shortDescription:
        "Track all income & expenses with smart categorization.",
      highlight: "Automatic category recognition",
      color: "#3B82F6",
      gradient: "linear-gradient(135deg, #3B82F6, #2563EB)",
    },
    {
      id: 2,
      icon: <FiPieChart />,
      title: "Deep Financial Analysis",
      shortTitle: "Financial Analysis",
      description:
        "See exactly where your money goes with detailed breakdowns, period comparisons, and category insights.",
      shortDescription: "Detailed spending analysis & category breakdowns.",
      highlight: "Real-time spending analytics",
      color: "#8B5CF6",
      gradient: "linear-gradient(135deg, #8B5CF6, #7C3AED)",
    },
    {
      id: 3,
      icon: <FiTarget />,
      title: "Budget Limits & Alerts",
      shortTitle: "Budget Alerts",
      description:
        "Set weekly/monthly spending limits for each category. Get notified before you exceed your budget.",
      shortDescription: "Set budgets & get alerts before overspending.",
      highlight: "95% budget adherence rate",
      color: "#10B981",
      gradient: "linear-gradient(135deg, #10B981, #059669)",
    },
    {
      id: 4,
      icon: <FiTrendingUp />,
      title: "Visual Trend Analysis",
      shortTitle: "Trend Analysis",
      description:
        "Interactive line charts showing spending trends, income growth, and financial forecasts.",
      shortDescription: "Interactive charts for spending & income trends.",
      highlight: "30-day trend predictions",
      color: "#F59E0B",
      gradient: "linear-gradient(135deg, #F59E0B, #D97706)",
    },
  ];

  useEffect(() => {
    const sequence = async () => {
      await chartControls.start({
        pathLength: [0, 1],
        opacity: [0, 1],
        transition: {
          duration: 2,
          ease: "easeInOut",
        },
      });

      await spendingControls.start({
        height: ["0%", "70%", "85%", "60%", "45%", "28%", "25%"],
        transition: {
          duration: 1.5,
          ease: "easeInOut",
          times: [0, 0.2, 0.4, 0.6, 0.8, 0.9, 1],
        },
      });

      await incomeControls.start({
        width: "80%",
        transition: {
          duration: 1.5,
          ease: "easeInOut",
        },
      });
    };

    sequence();

    // Simulate budget check
    const totalSpent = spendingCategories.reduce(
      (sum, cat) => sum + cat.amount,
      0
    );
    if (totalSpent > 1500) {
      setBudgetExceeded(true);
    }
  }, [chartControls, spendingControls, incomeControls]);

  const handleFeatureClick = (index) => {
    setActiveView(["overview", "analysis", "budget", "trends"][index]);
  };

  const getCurrentFeature = () =>
    features.find(
      (_, index) =>
        ["overview", "analysis", "budget", "trends"][index] === activeView
    ) || features[0];

  return (
    <ShowcaseContainer>
      {/* Animated Background */}
      <BackgroundGrid />
      <FloatingCoins />

      <ContentWrapper>
        <HeaderSection>
          <Badge $isSmallScreen={isSmallScreen}>
            <FiDollarSign />
            <span>
              {isSmallScreen
                ? "FINANCE MANAGEMENT"
                : "SMART FINANCE MANAGEMENT"}
            </span>
          </Badge>
          <MainTitle>
            Take Control of Your
            <GradientText>Money</GradientText>
          </MainTitle>
          <Subtitle $isSmallScreen={isSmallScreen}>
            Track every transaction, analyze spending patterns, set smart
            budgets, and visualize your financial journey with intelligent
            insights.
          </Subtitle>
        </HeaderSection>

        {/* Period Selector - Optimized for mobile */}
        <PeriodSelector>
          {["day", "week", "month", "year"].map((period) => (
            <PeriodButton
              key={period}
              active={selectedPeriod === period}
              onClick={() => setSelectedPeriod(period)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              $isSmallScreen={isSmallScreen}
            >
              {period.charAt(0).toUpperCase() + period.slice(1)}
            </PeriodButton>
          ))}
        </PeriodSelector>

        <ShowcaseGrid>
          {/* Features Navigation */}
          <FeatureNav>
            {features.map((feature, index) => (
              <FeatureNavItem
                key={feature.id}
                active={
                  activeView ===
                  ["overview", "analysis", "budget", "trends"][index]
                }
                onClick={() => handleFeatureClick(index)}
                color={feature.color}
                gradient={feature.gradient}
                whileHover={{ x: isSmallScreen ? 0 : 5 }}
                $isSmallScreen={isSmallScreen}
              >
                <NavIcon
                  active={
                    activeView ===
                    ["overview", "analysis", "budget", "trends"][index]
                  }
                  gradient={feature.gradient}
                  $isSmallScreen={isSmallScreen}
                >
                  {feature.icon}
                </NavIcon>
                <NavContent>
                  <NavTitle $isSmallScreen={isSmallScreen}>
                    {isSmallScreen ? feature.shortTitle : feature.title}
                  </NavTitle>
                  {!isSmallScreen && (
                    <NavHighlight>{feature.highlight}</NavHighlight>
                  )}
                </NavContent>
                {activeView ===
                  ["overview", "analysis", "budget", "trends"][index] &&
                  !isSmallScreen && (
                    <ActiveIndicator
                      layoutId="activeIndicator"
                      gradient={feature.gradient}
                    />
                  )}
              </FeatureNavItem>
            ))}
          </FeatureNav>

          {/* Main Financial Dashboard */}
          <FinancialDashboard $isSmallScreen={isSmallScreen}>
            <DashboardHeader $isSmallScreen={isSmallScreen}>
              <DashboardTitle $isSmallScreen={isSmallScreen}>
                {isSmallScreen ? "Dashboard" : "Financial Dashboard"}
                <PeriodBadge $isSmallScreen={isSmallScreen}>
                  {selectedPeriod.charAt(0).toUpperCase() +
                    selectedPeriod.slice(1)}
                </PeriodBadge>
              </DashboardTitle>
              <DashboardActions $isSmallScreen={isSmallScreen}>
                {!isSmallScreen && (
                  <>
                    <ActionButton whileHover={{ scale: 1.05 }}>
                      <FiFilter />
                      <span>Filter</span>
                    </ActionButton>
                    <ActionButton whileHover={{ scale: 1.05 }}>
                      <FiDownload />
                      <span>Export</span>
                    </ActionButton>
                  </>
                )}
              </DashboardActions>
            </DashboardHeader>

            <DashboardGrid>
              {/* Financial Overview Cards - Optimized for mobile */}
              <OverviewCards $isSmallScreen={isSmallScreen}>
                <BalanceCard $isSmallScreen={isSmallScreen}>
                  <CardHeader $isSmallScreen={isSmallScreen}>
                    <FiTrendingUp />
                    <span>Current Balance</span>
                  </CardHeader>
                  <BalanceAmount $isSmallScreen={isSmallScreen}>
                    $4,258.75
                  </BalanceAmount>
                  <BalanceTrend positive $isSmallScreen={isSmallScreen}>
                    {isSmallScreen ? "+12.5%" : "+12.5% from last month"}
                  </BalanceTrend>
                </BalanceCard>

                <IncomeCard $isSmallScreen={isSmallScreen}>
                  <CardHeader $isSmallScreen={isSmallScreen}>
                    <FiTrendingUp />
                    <span>Total Income</span>
                  </CardHeader>
                  <IncomeAmount $isSmallScreen={isSmallScreen}>
                    $4,350.00
                  </IncomeAmount>
                  <IncomeProgress>
                    <ProgressBar $isSmallScreen={isSmallScreen}>
                      <ProgressFill
                        animate={incomeControls}
                        gradient={getCurrentFeature().gradient}
                      />
                    </ProgressBar>
                    <ProgressLabel $isSmallScreen={isSmallScreen}>
                      {isSmallScreen ? "87% Target" : "Monthly Target: 87%"}
                    </ProgressLabel>
                  </IncomeProgress>
                </IncomeCard>

                <ExpenseCard $isSmallScreen={isSmallScreen}>
                  <CardHeader $isSmallScreen={isSmallScreen}>
                    <FiTrendingDown />
                    <span>Total Expenses</span>
                  </CardHeader>
                  <ExpenseAmount $isSmallScreen={isSmallScreen}>
                    $2,375.50
                  </ExpenseAmount>
                  <BudgetStatus
                    budgetExceeded={budgetExceeded}
                    $isSmallScreen={isSmallScreen}
                  >
                    {budgetExceeded ? (
                      <>
                        <FiBell />
                        <span>
                          {isSmallScreen
                            ? "Over Budget"
                            : "Over budget by $125.50"}
                        </span>
                      </>
                    ) : (
                      <>
                        <FiCheckCircle />
                        <span>
                          {isSmallScreen ? "On Budget" : "Within budget"}
                        </span>
                      </>
                    )}
                  </BudgetStatus>
                </ExpenseCard>

                <SavingsCard $isSmallScreen={isSmallScreen}>
                  <CardHeader $isSmallScreen={isSmallScreen}>
                    <FiDollarSign />
                    <span>{isSmallScreen ? "Savings" : "Monthly Savings"}</span>
                  </CardHeader>
                  <SavingsAmount $isSmallScreen={isSmallScreen}>
                    $1,974.50
                  </SavingsAmount>
                  <SavingsRate $isSmallScreen={isSmallScreen}>
                    {isSmallScreen ? "45%" : "45% of income"}
                  </SavingsRate>
                </SavingsCard>
              </OverviewCards>

              {/* Spending Categories Chart */}
              <SpendingAnalysis $isSmallScreen={isSmallScreen}>
                <AnalysisHeader $isSmallScreen={isSmallScreen}>
                  <h3>Spending by Category</h3>
                  {!isSmallScreen && <ViewAll>View Details →</ViewAll>}
                </AnalysisHeader>
                <CategoriesGrid $isSmallScreen={isSmallScreen}>
                  {spendingCategories.map((category) => (
                    <CategoryItem
                      key={category.id}
                      $isSmallScreen={isSmallScreen}
                    >
                      <CategoryHeader>
                        <CategoryIcon
                          color={category.color}
                          $isSmallScreen={isSmallScreen}
                        >
                          {category.icon}
                        </CategoryIcon>
                        <CategoryInfo>
                          <CategoryName $isSmallScreen={isSmallScreen}>
                            {isSmallScreen ? category.shortName : category.name}
                          </CategoryName>
                          <CategoryAmount $isSmallScreen={isSmallScreen}>
                            ${category.amount}
                          </CategoryAmount>
                        </CategoryInfo>
                      </CategoryHeader>
                      <CategoryProgress $isSmallScreen={isSmallScreen}>
                        <ProgressTrack $isSmallScreen={isSmallScreen}>
                          <ProgressFill
                            style={{ width: `${category.percentage}%` }}
                            color={category.color}
                          />
                        </ProgressTrack>
                        <Percentage $isSmallScreen={isSmallScreen}>
                          {category.percentage}%
                        </Percentage>
                      </CategoryProgress>
                    </CategoryItem>
                  ))}
                </CategoriesGrid>
              </SpendingAnalysis>

              {/* Interactive Line Chart */}
              <TrendChart $isSmallScreen={isSmallScreen}>
                <ChartHeader $isSmallScreen={isSmallScreen}>
                  <h3>Spending Trends</h3>
                  {!isSmallScreen && (
                    <ChartLegend>
                      <LegendItem color="#3B82F6">
                        <div className="dot" />
                        <span>This Month</span>
                      </LegendItem>
                      <LegendItem color="#8B5CF6">
                        <div className="dot" />
                        <span>Last Month</span>
                      </LegendItem>
                    </ChartLegend>
                  )}
                </ChartHeader>
                <ChartContainer $isSmallScreen={isSmallScreen}>
                  {/* Simplified Chart Visualization */}
                  {!isSmallScreen && (
                    <ChartGrid>
                      {["$400", "$300", "$200", "$100", "$0"].map(
                        (label, i) => (
                          <GridLine key={i}>
                            <GridLabel>{label}</GridLabel>
                          </GridLine>
                        )
                      )}
                    </ChartGrid>
                  )}
                  <ChartBars $isSmallScreen={isSmallScreen}>
                    {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map(
                      (day, i) => (
                        <ChartColumn key={i} $isSmallScreen={isSmallScreen}>
                          <BarLabel $isSmallScreen={isSmallScreen}>
                            {isSmallScreen ? day.charAt(0) : day}
                          </BarLabel>
                          <BarGroup $isSmallScreen={isSmallScreen}>
                            <CurrentBar
                              animate={spendingControls}
                              custom={i}
                              color="#3B82F6"
                              $isSmallScreen={isSmallScreen}
                            />
                            {!isSmallScreen && (
                              <PreviousBar
                                animate={spendingControls}
                                custom={i}
                                color="#8B5CF6"
                              />
                            )}
                          </BarGroup>
                        </ChartColumn>
                      )
                    )}
                  </ChartBars>
                </ChartContainer>
              </TrendChart>

              {/* Recent Transactions */}
              <RecentTransactions $isSmallScreen={isSmallScreen}>
                <TransactionsHeader $isSmallScreen={isSmallScreen}>
                  <h3>Recent Transactions</h3>
                  <AddTransaction
                    whileHover={{ scale: 1.05 }}
                    $isSmallScreen={isSmallScreen}
                  >
                    <FiDollarSign />
                    <span>{isSmallScreen ? "Add" : "Add New"}</span>
                  </AddTransaction>
                </TransactionsHeader>
                <TransactionsList>
                  {recentTransactions.map((transaction) => (
                    <TransactionItem
                      key={transaction.id}
                      type={transaction.type}
                      $isSmallScreen={isSmallScreen}
                    >
                      <TransactionIcon
                        type={transaction.type}
                        $isSmallScreen={isSmallScreen}
                      >
                        {transaction.type === "income" ? (
                          <FiTrendingUp />
                        ) : (
                          <FiTrendingDown />
                        )}
                      </TransactionIcon>
                      <TransactionInfo>
                        <TransactionName $isSmallScreen={isSmallScreen}>
                          {isSmallScreen
                            ? transaction.shortName
                            : transaction.name}
                        </TransactionName>
                        <TransactionMeta $isSmallScreen={isSmallScreen}>
                          <span className="category">
                            {isSmallScreen
                              ? transaction.shortCategory
                              : transaction.category}
                          </span>
                          <span className="date">{transaction.date}</span>
                        </TransactionMeta>
                      </TransactionInfo>
                      <TransactionAmount
                        type={transaction.type}
                        $isSmallScreen={isSmallScreen}
                      >
                        {transaction.type === "income" ? "+" : "-"}$
                        {Math.abs(transaction.amount).toLocaleString()}
                      </TransactionAmount>
                    </TransactionItem>
                  ))}
                </TransactionsList>
              </RecentTransactions>
            </DashboardGrid>
          </FinancialDashboard>
        </ShowcaseGrid>
      </ContentWrapper>
    </ShowcaseContainer>
  );
};

// Animations
const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-10px) rotate(5deg); }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Styled Components with enhanced mobile responsiveness
const ShowcaseContainer = styled.section`
  padding: 8rem 8%;
  background: linear-gradient(135deg, #f8f9ff 0%, #ffffff 100%);
  min-height: 100vh;
  display: flex;
  align-items: center;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 4rem 5%;
  }

  @media (max-width: 430px) {
    padding: 3rem 4%;
    min-height: auto;
  }

  @media (max-width: 375px) {
    padding: 2.5rem 3%;
  }
`;

const BackgroundGrid = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: linear-gradient(
      rgba(146, 129, 221, 0.03) 1px,
      transparent 1px
    ),
    linear-gradient(90deg, rgba(146, 129, 221, 0.03) 1px, transparent 1px);
  background-size: 50px 50px;
  opacity: 0.5;

  @media (max-width: 430px) {
    background-size: 30px 30px;
  }
`;

const FloatingCoins = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  pointer-events: none;

  &::before,
  &::after {
    content: "💰";
    position: absolute;
    font-size: 2rem;
    opacity: 0.1;
    animation: ${float} 15s ease-in-out infinite;

    @media (max-width: 430px) {
      font-size: 1.5rem;
    }
  }

  &::before {
    top: 15%;
    left: 5%;
    animation-delay: 0s;

    @media (max-width: 430px) {
      top: 10%;
      left: 3%;
    }
  }

  &::after {
    bottom: 25%;
    right: 10%;
    content: "📈";
    animation-delay: 7.5s;

    @media (max-width: 430px) {
      bottom: 20%;
      right: 5%;
    }
  }
`;

const ContentWrapper = styled.div`
  max-width: 1600px;
  margin: 0 auto;
  width: 100%;
  position: relative;
  z-index: 1;
`;

const HeaderSection = styled.div`
  text-align: center;
  margin-bottom: 4rem;
  animation: ${fadeInUp} 0.8s ease-out;

  @media (max-width: 768px) {
    margin-bottom: 3rem;
  }

  @media (max-width: 430px) {
    margin-bottom: 2rem;
  }
`;

const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1.5rem;
  background: rgba(146, 129, 221, 0.1);
  border: 1px solid rgba(146, 129, 221, 0.3);
  border-radius: 100px;
  color: #9281dd;
  font-size: ${({ $isSmallScreen }) =>
    $isSmallScreen ? "0.8125rem" : "0.875rem"};
  font-weight: 600;
  letter-spacing: 1px;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    padding: 0.5rem 1.25rem;
    margin-bottom: 1.5rem;
  }

  @media (max-width: 430px) {
    padding: ${({ $isSmallScreen }) =>
      $isSmallScreen ? "0.375rem 1rem" : "0.5rem 1.25rem"};
    font-size: ${({ $isSmallScreen }) =>
      $isSmallScreen ? "0.75rem" : "0.8125rem"};
    margin-bottom: 1.25rem;
    gap: ${({ $isSmallScreen }) => ($isSmallScreen ? "0.375rem" : "0.5rem")};
  }

  @media (max-width: 375px) {
    padding: 0.375rem 0.875rem;
  }
`;

const MainTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: 300;
  line-height: 1.2;
  color: #1a1a2e;
  margin-bottom: 1.5rem;
  font-family: "Montserrat", sans-serif;

  @media (max-width: 768px) {
    font-size: 2.5rem;
    margin-bottom: 1.25rem;
  }

  @media (max-width: 430px) {
    font-size: 2rem;
    margin-bottom: 1rem;
    line-height: 1.1;
  }

  @media (max-width: 375px) {
    font-size: 1.75rem;
  }
`;

const GradientText = styled.span`
  color: #9281dd;
  margin: 0 8px;
  font-weight: 400;
  font-size: 4rem;

  @media (max-width: 768px) {
    font-size: 3rem;
    margin: 0 4px;
  }

  @media (max-width: 430px) {
    font-size: 2.25rem;
    margin: 0 3px;
  }

  @media (max-width: 375px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.25rem;
  line-height: 1.6;
  color: #4a5568;
  max-width: 700px;
  margin: 0 auto;
  opacity: 0.9;

  @media (max-width: 768px) {
    font-size: 1.125rem;
    padding: 0 1rem;
    line-height: 1.5;
  }

  @media (max-width: 430px) {
    font-size: ${({ $isSmallScreen }) =>
      $isSmallScreen ? "1rem" : "1.125rem"};
    padding: ${({ $isSmallScreen }) =>
      $isSmallScreen ? "0 0.5rem" : "0 1rem"};
    line-height: 1.4;
  }

  @media (max-width: 375px) {
    font-size: 0.9375rem;
  }
`;

const PeriodSelector = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.75rem;
  margin-bottom: 3rem;
  flex-wrap: wrap;
  animation: ${fadeInUp} 0.8s ease-out 0.2s both;

  @media (max-width: 768px) {
    gap: 0.5rem;
    margin-bottom: 2rem;
  }

  @media (max-width: 430px) {
    gap: ${({ $isSmallScreen }) => ($isSmallScreen ? "0.375rem" : "0.5rem")};
    margin-bottom: ${({ $isSmallScreen }) =>
      $isSmallScreen ? "1.5rem" : "2rem"};
  }
`;

const PeriodButton = styled(motion.button)`
  padding: 0.75rem 1.5rem;
  background: ${({ active }) => (active ? "#ffffff" : "transparent")};
  border: 1px solid
    ${({ active }) =>
      active ? "rgba(146, 129, 221, 0.3)" : "rgba(0, 0, 0, 0.1)"};
  border-radius: 8px;
  font-weight: 600;
  color: ${({ active }) => (active ? "#9281dd" : "#4a5568")};
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9375rem;

  &:hover {
    background: #ffffff;
    border-color: rgba(146, 129, 221, 0.3);
  }

  @media (max-width: 768px) {
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
  }

  @media (max-width: 430px) {
    padding: ${({ $isSmallScreen }) =>
      $isSmallScreen ? "0.5rem 0.75rem" : "0.5rem 1rem"};
    font-size: ${({ $isSmallScreen }) =>
      $isSmallScreen ? "0.8125rem" : "0.875rem"};
    border-radius: ${({ $isSmallScreen }) => ($isSmallScreen ? "6px" : "8px")};
  }

  @media (max-width: 375px) {
    padding: 0.375rem 0.625rem;
    font-size: 0.75rem;
  }
`;

const ShowcaseGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 3fr;
  gap: 4rem;
  margin-bottom: 4rem;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr 2fr;
  }

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }

  @media (max-width: 430px) {
    gap: ${({ $isSmallScreen }) => ($isSmallScreen ? "2rem" : "3rem")};
    margin-bottom: ${({ $isSmallScreen }) =>
      $isSmallScreen ? "3rem" : "4rem"};
  }

  @media (max-width: 375px) {
    gap: 1.5rem;
    margin-bottom: 2.5rem;
  }
`;

const FeatureNav = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  @media (max-width: 1024px) {
    order: 2;
  }

  @media (max-width: 768px) {
    gap: 0.75rem;
  }

  @media (max-width: 430px) {
    gap: ${({ $isSmallScreen }) => ($isSmallScreen ? "0.5rem" : "0.75rem")};
  }
`;

const FeatureNavItem = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem;
  background: ${({ active }) => (active ? "#ffffff" : "transparent")};
  border: 1px solid
    ${({ active }) =>
      active ? "rgba(146, 129, 221, 0.2)" : "rgba(0, 0, 0, 0.1)"};
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  box-shadow: ${({ active }) =>
    active ? "0 10px 40px rgba(146, 129, 221, 0.1)" : "none"};

  &:hover {
    background: #ffffff;
    transform: translateX(
      ${({ $isSmallScreen }) => ($isSmallScreen ? "0" : "5px")}
    );
    box-shadow: 0 10px 30px rgba(146, 129, 221, 0.1);
    border-color: rgba(146, 129, 221, 0.3);
  }

  ${({ active, gradient }) =>
    active &&
    css`
      &::before {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 2px;
        background: ${gradient};
      }
    `}

  @media (max-width: 768px) {
    padding: 1rem;
    gap: 0.75rem;
  }

  @media (max-width: 430px) {
    padding: ${({ $isSmallScreen }) => ($isSmallScreen ? "0.875rem" : "1rem")};
    gap: ${({ $isSmallScreen }) => ($isSmallScreen ? "0.625rem" : "0.75rem")};
    border-radius: ${({ $isSmallScreen }) =>
      $isSmallScreen ? "12px" : "16px"};
  }

  @media (max-width: 375px) {
    padding: 0.75rem;
    gap: 0.5rem;
    border-radius: 10px;
  }
`;

const NavIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  background: ${({ active, gradient }) =>
    active ? gradient : "rgba(146, 129, 221, 0.1)"};
  color: ${({ active }) => (active ? "#ffffff" : "#9281dd")};
  transition: all 0.3s ease;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    font-size: 1.25rem;
  }

  @media (max-width: 430px) {
    width: ${({ $isSmallScreen }) => ($isSmallScreen ? "36px" : "40px")};
    height: ${({ $isSmallScreen }) => ($isSmallScreen ? "36px" : "40px")};
    font-size: ${({ $isSmallScreen }) =>
      $isSmallScreen ? "1.125rem" : "1.25rem"};
    border-radius: ${({ $isSmallScreen }) =>
      $isSmallScreen ? "10px" : "12px"};
  }

  @media (max-width: 375px) {
    width: 32px;
    height: 32px;
    font-size: 1rem;
    border-radius: 8px;
  }
`;

const NavContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const NavTitle = styled.div`
  font-weight: 600;
  color: #1a1a2e;
  margin-bottom: ${({ $isSmallScreen }) => ($isSmallScreen ? "0" : "0.25rem")};
  font-size: ${({ $isSmallScreen }) =>
    $isSmallScreen ? "0.9375rem" : "1.125rem"};
  line-height: ${({ $isSmallScreen }) => ($isSmallScreen ? "1.2" : "1.4")};

  @media (max-width: 768px) {
    font-size: 1rem;
  }

  @media (max-width: 430px) {
    font-size: ${({ $isSmallScreen }) =>
      $isSmallScreen ? "0.875rem" : "0.9375rem"};
  }

  @media (max-width: 375px) {
    font-size: 0.8125rem;
  }
`;

const NavHighlight = styled.div`
  font-size: 0.875rem;
  color: #718096;

  @media (max-width: 768px) {
    display: none;
  }
`;

const ActiveIndicator = styled(motion.div)`
  position: absolute;
  right: 1.25rem;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ gradient }) => gradient};

  @media (max-width: 768px) {
    display: none;
  }
`;

const FinancialDashboard = styled.div`
  background: #ffffff;
  border: 1px solid rgba(146, 129, 221, 0.2);
  border-radius: 24px;
  padding: 2rem;
  box-shadow: 0 20px 60px rgba(146, 129, 221, 0.1),
    0 10px 30px rgba(0, 0, 0, 0.05);
  animation: ${fadeInUp} 0.8s ease-out 0.4s both;

  @media (max-width: 768px) {
    padding: 1.5rem;
    border-radius: 16px;
  }

  @media (max-width: 430px) {
    padding: ${({ $isSmallScreen }) => ($isSmallScreen ? "1.25rem" : "1.5rem")};
    border-radius: ${({ $isSmallScreen }) =>
      $isSmallScreen ? "14px" : "16px"};
  }

  @media (max-width: 375px) {
    padding: 1rem;
    border-radius: 12px;
  }
`;

const DashboardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
    margin-bottom: 1.5rem;
  }

  @media (max-width: 430px) {
    gap: ${({ $isSmallScreen }) => ($isSmallScreen ? "0.75rem" : "1rem")};
    margin-bottom: ${({ $isSmallScreen }) =>
      $isSmallScreen ? "1.25rem" : "1.5rem"};
  }
`;

const DashboardTitle = styled.h2`
  font-size: ${({ $isSmallScreen }) => ($isSmallScreen ? "1.5rem" : "1.75rem")};
  font-weight: 700;
  color: #1a1a2e;
  display: flex;
  align-items: center;
  gap: 1rem;
  margin: 0;

  @media (max-width: 430px) {
    gap: ${({ $isSmallScreen }) => ($isSmallScreen ? "0.5rem" : "1rem")};
  }

  @media (max-width: 375px) {
    font-size: 1.25rem;
    gap: 0.5rem;
  }
`;

const PeriodBadge = styled.span`
  background: rgba(146, 129, 221, 0.1);
  color: #9281dd;
  padding: 0.25rem 0.75rem;
  border-radius: 100px;
  font-size: ${({ $isSmallScreen }) =>
    $isSmallScreen ? "0.75rem" : "0.875rem"};
  font-weight: 600;

  @media (max-width: 375px) {
    padding: 0.1875rem 0.5rem;
    font-size: 0.6875rem;
  }
`;

const DashboardActions = styled.div`
  display: flex;
  gap: 0.75rem;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-start;
  }

  @media (max-width: 430px) {
    gap: ${({ $isSmallScreen }) => ($isSmallScreen ? "0.5rem" : "0.75rem")};
  }
`;

const ActionButton = styled(motion.button)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  background: rgba(146, 129, 221, 0.05);
  border: 1px solid rgba(146, 129, 221, 0.2);
  border-radius: 8px;
  color: #9281dd;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(146, 129, 221, 0.1);
    border-color: #9281dd;
  }

  @media (max-width: 430px) {
    padding: ${({ $isSmallScreen }) =>
      $isSmallScreen ? "0.5rem 0.875rem" : "0.75rem 1.25rem"};
    font-size: ${({ $isSmallScreen }) =>
      $isSmallScreen ? "0.8125rem" : "0.875rem"};
    gap: ${({ $isSmallScreen }) => ($isSmallScreen ? "0.375rem" : "0.5rem")};
  }

  @media (max-width: 375px) {
    padding: 0.5rem 0.75rem;
    font-size: 0.75rem;
    gap: 0.25rem;
  }
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: auto auto;
  gap: 2rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }

  @media (max-width: 430px) {
    gap: ${({ $isSmallScreen }) => ($isSmallScreen ? "1.5rem" : "2rem")};
  }

  @media (max-width: 375px) {
    gap: 1.25rem;
  }
`;

const OverviewCards = styled.div`
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: ${({ $isSmallScreen }) =>
    $isSmallScreen ? "1fr" : "repeat(4, 1fr)"};
  gap: ${({ $isSmallScreen }) => ($isSmallScreen ? "1rem" : "1.5rem")};

  @media (max-width: 1200px) and (min-width: 1025px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) and (min-width: 431px) {
    grid-template-columns: 1fr;
  }
`;

const BaseCard = styled.div`
  background: #ffffff;
  border: 1px solid rgba(146, 129, 221, 0.1);
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 30px rgba(146, 129, 221, 0.1);
  }

  @media (max-width: 430px) {
    padding: ${({ $isSmallScreen }) => ($isSmallScreen ? "1.25rem" : "1.5rem")};
    border-radius: ${({ $isSmallScreen }) =>
      $isSmallScreen ? "14px" : "16px"};
  }

  @media (max-width: 375px) {
    padding: 1rem;
    border-radius: 12px;
  }
`;

const BalanceCard = styled(BaseCard)`
  border-top: 4px solid #10b981;
`;

const IncomeCard = styled(BaseCard)`
  border-top: 4px solid #3b82f6;
`;

const ExpenseCard = styled(BaseCard)`
  border-top: 4px solid #ef4444;
`;

const SavingsCard = styled(BaseCard)`
  border-top: 4px solid #f59e0b;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #4a5568;
  font-size: ${({ $isSmallScreen }) =>
    $isSmallScreen ? "0.8125rem" : "0.875rem"};
  font-weight: 600;
  margin-bottom: 1rem;

  @media (max-width: 375px) {
    gap: 0.5rem;
    margin-bottom: 0.75rem;
  }
`;

const BalanceAmount = styled.div`
  font-size: ${({ $isSmallScreen }) => ($isSmallScreen ? "1.75rem" : "2rem")};
  font-weight: 800;
  color: #1a1a2e;
  margin-bottom: 0.5rem;
  line-height: 1;

  @media (max-width: 375px) {
    font-size: 1.5rem;
    margin-bottom: 0.375rem;
  }
`;

const BalanceTrend = styled.div`
  font-size: ${({ $isSmallScreen }) =>
    $isSmallScreen ? "0.8125rem" : "0.875rem"};
  color: ${({ positive }) => (positive ? "#10B981" : "#EF4444")};
  font-weight: 600;

  @media (max-width: 375px) {
    font-size: 0.75rem;
  }
`;

const IncomeAmount = styled.div`
  font-size: ${({ $isSmallScreen }) => ($isSmallScreen ? "1.75rem" : "2rem")};
  font-weight: 800;
  color: #1a1a2e;
  margin-bottom: 1rem;
  line-height: 1;

  @media (max-width: 375px) {
    font-size: 1.5rem;
    margin-bottom: 0.75rem;
  }
`;

const IncomeProgress = styled.div``;

const ExpenseAmount = styled.div`
  font-size: ${({ $isSmallScreen }) => ($isSmallScreen ? "1.75rem" : "2rem")};
  font-weight: 800;
  color: #1a1a2e;
  margin-bottom: 1rem;
  line-height: 1;

  @media (max-width: 375px) {
    font-size: 1.5rem;
    margin-bottom: 0.75rem;
  }
`;

const BudgetStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: ${({ budgetExceeded }) =>
    budgetExceeded ? "rgba(239, 68, 68, 0.1)" : "rgba(16, 185, 129, 0.1)"};
  color: ${({ budgetExceeded }) => (budgetExceeded ? "#EF4444" : "#10B981")};
  border-radius: 8px;
  font-size: ${({ $isSmallScreen }) =>
    $isSmallScreen ? "0.8125rem" : "0.875rem"};
  font-weight: 600;
  border: 1px solid
    ${({ budgetExceeded }) =>
      budgetExceeded ? "rgba(239, 68, 68, 0.2)" : "rgba(16, 185, 129, 0.2)"};

  @media (max-width: 375px) {
    padding: 0.375rem 0.5rem;
    font-size: 0.75rem;
    gap: 0.375rem;
  }
`;

const SavingsAmount = styled.div`
  font-size: ${({ $isSmallScreen }) => ($isSmallScreen ? "1.75rem" : "2rem")};
  font-weight: 800;
  color: #1a1a2e;
  margin-bottom: 0.5rem;
  line-height: 1;

  @media (max-width: 375px) {
    font-size: 1.5rem;
    margin-bottom: 0.375rem;
  }
`;

const SavingsRate = styled.div`
  font-size: ${({ $isSmallScreen }) =>
    $isSmallScreen ? "0.8125rem" : "0.875rem"};
  color: #718096;
  font-weight: 600;

  @media (max-width: 375px) {
    font-size: 0.75rem;
  }
`;

const ProgressBar = styled.div`
  height: 8px;
  background: rgba(146, 129, 221, 0.1);
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 0.5rem;

  @media (max-width: 430px) {
    height: ${({ $isSmallScreen }) => ($isSmallScreen ? "6px" : "8px")};
  }

  @media (max-width: 375px) {
    height: 6px;
    margin-bottom: 0.375rem;
  }
`;

const ProgressFill = styled(motion.div)`
  height: 100%;
  background: ${({ gradient, color }) => gradient || color || "#9281dd"};
  border-radius: 4px;
`;

const ProgressLabel = styled.div`
  font-size: ${({ $isSmallScreen }) =>
    $isSmallScreen ? "0.75rem" : "0.8125rem"};
  color: #718096;

  @media (max-width: 375px) {
    font-size: 0.6875rem;
  }
`;

const SpendingAnalysis = styled.div`
  grid-column: 1;
  background: #ffffff;
  border: 1px solid rgba(146, 129, 221, 0.1);
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);

  @media (max-width: 1024px) {
    grid-column: 1 / -1;
  }

  @media (max-width: 430px) {
    padding: ${({ $isSmallScreen }) => ($isSmallScreen ? "1.25rem" : "1.5rem")};
    border-radius: ${({ $isSmallScreen }) =>
      $isSmallScreen ? "14px" : "16px"};
  }

  @media (max-width: 375px) {
    padding: 1rem;
    border-radius: 12px;
  }
`;

const AnalysisHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;

  h3 {
    font-size: ${({ $isSmallScreen }) =>
      $isSmallScreen ? "1.125rem" : "1.25rem"};
    font-weight: 700;
    color: #1a1a2e;
    margin: 0;
  }

  @media (max-width: 375px) {
    margin-bottom: 1rem;

    h3 {
      font-size: 1rem;
    }
  }
`;

const ViewAll = styled.button`
  background: none;
  border: none;
  color: #9281dd;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    color: #6d5bd4;
  }
`;

const CategoriesGrid = styled.div`
  display: grid;
  grid-template-columns: ${({ $isSmallScreen }) =>
    $isSmallScreen ? "1fr" : "repeat(2, 1fr)"};
  gap: ${({ $isSmallScreen }) => ($isSmallScreen ? "0.75rem" : "1rem")};

  @media (max-width: 768px) and (min-width: 431px) {
    grid-template-columns: 1fr;
  }
`;

const CategoryItem = styled.div`
  padding: ${({ $isSmallScreen }) => ($isSmallScreen ? "0.875rem" : "1rem")};
  background: rgba(146, 129, 221, 0.03);
  border: 1px solid rgba(146, 129, 221, 0.1);
  border-radius: 12px;

  @media (max-width: 430px) {
    border-radius: ${({ $isSmallScreen }) =>
      $isSmallScreen ? "10px" : "12px"};
  }

  @media (max-width: 375px) {
    padding: 0.75rem;
    border-radius: 8px;
  }
`;

const CategoryHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.75rem;

  @media (max-width: 430px) {
    gap: ${({ $isSmallScreen }) => ($isSmallScreen ? "0.75rem" : "1rem")};
  }

  @media (max-width: 375px) {
    gap: 0.625rem;
    margin-bottom: 0.5rem;
  }
`;

const CategoryIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ color }) => `${color}15`};
  color: ${({ color }) => color};
  font-size: 1.25rem;

  @media (max-width: 430px) {
    width: ${({ $isSmallScreen }) => ($isSmallScreen ? "36px" : "40px")};
    height: ${({ $isSmallScreen }) => ($isSmallScreen ? "36px" : "40px")};
    font-size: ${({ $isSmallScreen }) =>
      $isSmallScreen ? "1.125rem" : "1.25rem"};
    border-radius: ${({ $isSmallScreen }) => ($isSmallScreen ? "8px" : "10px")};
  }

  @media (max-width: 375px) {
    width: 32px;
    height: 32px;
    font-size: 1rem;
    border-radius: 8px;
  }
`;

const CategoryInfo = styled.div`
  flex: 1;
`;

const CategoryName = styled.div`
  font-weight: 600;
  color: #1a1a2e;
  font-size: ${({ $isSmallScreen }) =>
    $isSmallScreen ? "0.875rem" : "0.9375rem"};

  @media (max-width: 375px) {
    font-size: 0.8125rem;
  }
`;

const CategoryAmount = styled.div`
  font-size: ${({ $isSmallScreen }) => ($isSmallScreen ? "1rem" : "1.125rem")};
  font-weight: 700;
  color: #1a1a2e;

  @media (max-width: 375px) {
    font-size: 0.9375rem;
  }
`;

const CategoryProgress = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;

  @media (max-width: 375px) {
    gap: 0.5rem;
  }
`;

const ProgressTrack = styled.div`
  flex: 1;
  height: 6px;
  background: rgba(146, 129, 221, 0.1);
  border-radius: 3px;
  overflow: hidden;

  @media (max-width: 430px) {
    height: ${({ $isSmallScreen }) => ($isSmallScreen ? "4px" : "6px")};
  }

  @media (max-width: 375px) {
    height: 4px;
  }
`;

const Percentage = styled.div`
  font-size: ${({ $isSmallScreen }) =>
    $isSmallScreen ? "0.8125rem" : "0.875rem"};
  font-weight: 600;
  color: #718096;
  min-width: 40px;
  text-align: right;

  @media (max-width: 375px) {
    font-size: 0.75rem;
    min-width: 35px;
  }
`;

const TrendChart = styled.div`
  grid-column: 2;
  background: #ffffff;
  border: 1px solid rgba(146, 129, 221, 0.1);
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);

  @media (max-width: 1024px) {
    grid-column: 1 / -1;
  }

  @media (max-width: 430px) {
    padding: ${({ $isSmallScreen }) => ($isSmallScreen ? "1.25rem" : "1.5rem")};
    border-radius: ${({ $isSmallScreen }) =>
      $isSmallScreen ? "14px" : "16px"};
  }

  @media (max-width: 375px) {
    padding: 1rem;
    border-radius: 12px;
  }
`;

const ChartHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;

  h3 {
    font-size: ${({ $isSmallScreen }) =>
      $isSmallScreen ? "1.125rem" : "1.25rem"};
    font-weight: 700;
    color: #1a1a2e;
    margin: 0;
  }

  @media (max-width: 375px) {
    margin-bottom: 1rem;

    h3 {
      font-size: 1rem;
    }
  }
`;

const ChartLegend = styled.div`
  display: flex;
  gap: 1rem;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  color: #718096;

  .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${({ color }) => color};
  }
`;

const ChartContainer = styled.div`
  position: relative;
  height: ${({ $isSmallScreen }) => ($isSmallScreen ? "150px" : "200px")};
  padding-top: ${({ $isSmallScreen }) => ($isSmallScreen ? "1.5rem" : "2rem")};

  @media (max-width: 375px) {
    height: 140px;
    padding-top: 1.25rem;
  }
`;

const ChartGrid = styled.div`
  position: absolute;
  top: 0;
  left: 40px;
  right: 0;
  bottom: 40px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const GridLine = styled.div`
  position: relative;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
`;

const GridLabel = styled.span`
  position: absolute;
  left: -40px;
  top: -8px;
  font-size: 0.75rem;
  color: #718096;
`;

const ChartBars = styled.div`
  position: absolute;
  top: 0;
  left: ${({ $isSmallScreen }) => ($isSmallScreen ? "30px" : "40px")};
  right: 0;
  bottom: ${({ $isSmallScreen }) => ($isSmallScreen ? "30px" : "40px")};
  display: flex;
  justify-content: space-around;
  align-items: flex-end;
  padding: 0 ${({ $isSmallScreen }) => ($isSmallScreen ? "0.5rem" : "1rem")};
`;

const ChartColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ $isSmallScreen }) => ($isSmallScreen ? "0.25rem" : "0.5rem")};
  width: ${({ $isSmallScreen }) => ($isSmallScreen ? "28px" : "40px")};
`;

const BarLabel = styled.div`
  font-size: ${({ $isSmallScreen }) =>
    $isSmallScreen ? "0.6875rem" : "0.75rem"};
  color: #718096;
  text-align: center;

  @media (max-width: 375px) {
    font-size: 0.625rem;
  }
`;

const BarGroup = styled.div`
  display: flex;
  gap: ${({ $isSmallScreen }) => ($isSmallScreen ? "2px" : "4px")};
  width: 100%;
  justify-content: center;
`;

const CurrentBar = styled(motion.div)`
  width: ${({ $isSmallScreen }) => ($isSmallScreen ? "6px" : "8px")};
  height: ${({ custom, $isSmallScreen }) => {
    const heights = [70, 85, 60, 45, 28, 25, 65];
    const baseHeight = heights[custom % heights.length];
    return $isSmallScreen ? `${baseHeight * 0.85}%` : `${baseHeight}%`;
  }};
  background: ${({ color }) => color};
  border-radius: 4px 4px 0 0;

  @media (max-width: 375px) {
    width: 5px;
  }
`;

const PreviousBar = styled(motion.div)`
  width: 8px;
  height: ${({ custom }) => {
    const heights = [45, 65, 40, 35, 20, 18, 50];
    return `${heights[custom % heights.length]}%`;
  }};
  background: ${({ color }) => color};
  opacity: 0.6;
  border-radius: 4px 4px 0 0;
`;

const RecentTransactions = styled.div`
  grid-column: 1 / -1;
  background: #ffffff;
  border: 1px solid rgba(146, 129, 221, 0.1);
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);

  @media (max-width: 430px) {
    padding: ${({ $isSmallScreen }) => ($isSmallScreen ? "1.25rem" : "1.5rem")};
    border-radius: ${({ $isSmallScreen }) =>
      $isSmallScreen ? "14px" : "16px"};
  }

  @media (max-width: 375px) {
    padding: 1rem;
    border-radius: 12px;
  }
`;

const TransactionsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;

  h3 {
    font-size: ${({ $isSmallScreen }) =>
      $isSmallScreen ? "1.125rem" : "1.25rem"};
    font-weight: 700;
    color: #1a1a2e;
    margin: 0;
  }

  @media (max-width: 375px) {
    margin-bottom: 1rem;

    h3 {
      font-size: 1rem;
    }
  }
`;

const AddTransaction = styled(motion.button)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  background: linear-gradient(135deg, #9281dd, #6d5bd4);
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(146, 129, 221, 0.3);
  }

  @media (max-width: 430px) {
    padding: ${({ $isSmallScreen }) =>
      $isSmallScreen ? "0.5rem 0.875rem" : "0.75rem 1.25rem"};
    font-size: ${({ $isSmallScreen }) =>
      $isSmallScreen ? "0.8125rem" : "0.875rem"};
    gap: ${({ $isSmallScreen }) => ($isSmallScreen ? "0.375rem" : "0.5rem")};
    border-radius: ${({ $isSmallScreen }) => ($isSmallScreen ? "6px" : "8px")};
  }

  @media (max-width: 375px) {
    padding: 0.5rem 0.75rem;
    font-size: 0.75rem;
    gap: 0.25rem;
  }
`;

const TransactionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  @media (max-width: 430px) {
    gap: ${({ $isSmallScreen }) => ($isSmallScreen ? "0.5rem" : "0.75rem")};
  }

  @media (max-width: 375px) {
    gap: 0.375rem;
  }
`;

const TransactionItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: rgba(146, 129, 221, 0.03);
  border: 1px solid rgba(146, 129, 221, 0.1);
  border-radius: 12px;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(146, 129, 221, 0.08);
    transform: translateX(
      ${({ $isSmallScreen }) => ($isSmallScreen ? "3px" : "5px")}
    );
  }

  @media (max-width: 430px) {
    padding: ${({ $isSmallScreen }) => ($isSmallScreen ? "0.875rem" : "1rem")};
    gap: ${({ $isSmallScreen }) => ($isSmallScreen ? "0.75rem" : "1rem")};
    border-radius: ${({ $isSmallScreen }) =>
      $isSmallScreen ? "10px" : "12px"};
  }

  @media (max-width: 375px) {
    padding: 0.75rem;
    gap: 0.625rem;
    border-radius: 8px;
  }
`;

const TransactionIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ type }) =>
    type === "income" ? "rgba(16, 185, 129, 0.1)" : "rgba(239, 68, 68, 0.1)"};
  color: ${({ type }) => (type === "income" ? "#10B981" : "#EF4444")};
  font-size: 1.25rem;

  @media (max-width: 430px) {
    width: ${({ $isSmallScreen }) => ($isSmallScreen ? "36px" : "40px")};
    height: ${({ $isSmallScreen }) => ($isSmallScreen ? "36px" : "40px")};
    font-size: ${({ $isSmallScreen }) =>
      $isSmallScreen ? "1.125rem" : "1.25rem"};
    border-radius: ${({ $isSmallScreen }) => ($isSmallScreen ? "8px" : "10px")};
  }

  @media (max-width: 375px) {
    width: 32px;
    height: 32px;
    font-size: 1rem;
    border-radius: 8px;
  }
`;

const TransactionInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const TransactionName = styled.div`
  font-weight: 600;
  color: #1a1a2e;
  margin-bottom: 0.25rem;
  font-size: ${({ $isSmallScreen }) =>
    $isSmallScreen ? "0.875rem" : "0.9375rem"};

  @media (max-width: 375px) {
    font-size: 0.8125rem;
    margin-bottom: 0.125rem;
  }
`;

const TransactionMeta = styled.div`
  display: flex;
  gap: ${({ $isSmallScreen }) => ($isSmallScreen ? "0.75rem" : "1rem")};
  font-size: ${({ $isSmallScreen }) =>
    $isSmallScreen ? "0.6875rem" : "0.75rem"};
  color: #718096;

  .category {
    background: rgba(146, 129, 221, 0.1);
    padding: ${({ $isSmallScreen }) =>
      $isSmallScreen ? "0.125rem 0.375rem" : "0.125rem 0.5rem"};
    border-radius: 100px;
    white-space: nowrap;
  }

  @media (max-width: 375px) {
    gap: 0.5rem;
    font-size: 0.625rem;

    .category {
      padding: 0.125rem 0.375rem;
    }
  }
`;

const TransactionAmount = styled.div`
  font-size: ${({ $isSmallScreen }) => ($isSmallScreen ? "1rem" : "1.125rem")};
  font-weight: 700;
  color: ${({ type }) => (type === "income" ? "#10B981" : "#EF4444")};
  white-space: nowrap;

  @media (max-width: 375px) {
    font-size: 0.875rem;
  }
`;

export default FinancesShowcase;
